import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import { useState } from "react";
import * as Yup from "yup";
import { changePasswordService } from "../../../services/userService";
import { useAlert } from "../../../contexts/AlertContext";

const validationSchema = Yup.object({
  currentPassword: Yup.string()
    .min(8, "Minimum 8 characters")
    .matches(/[a-z]/, "At least one lowercase letter")
    .matches(/[A-Z]/, "At least one uppercase letter")
    .matches(/\d/, "At least one number")
    .matches(/[@$!%*?&#]/, "At least one special character")
    .required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Minimum 8 characters")
    .matches(/[a-z]/, "At least one lowercase letter")
    .matches(/[A-Z]/, "At least one uppercase letter")
    .matches(/\d/, "At least one number")
    .matches(/[@$!%*?&#]/, "At least one special character")
    .required("Password is required")
    .test(
      "not-same-as-current",
      "New password must be different from current password",
      function (value) {
        return value !== this.parent.currentPassword;
      }
    ),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Both passwords must match")
    .required("Confirm password is required")
});

const defaultData = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: ""
};

const ChangePassword = () => {
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });
  const { showAlert } = useAlert();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  };

  const PasswordToggle = (e, field) => {
    e.preventDefault();
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      changePasswordService({ old_password: formData.currentPassword, new_password: formData.newPassword }).then((res) => {
        setFormData(defaultData);
        showAlert({
          title: "Users Notification",
          message: res?.message ?? "something went wrong, try again later",
          variant: "success",
        });
      }).catch((error) =>
        showAlert({
          title: "Users Notification",
          message: error.response.data.message ?? "something went wrong, try again later",
          variant: "danger",
        })
      );

    } catch (validationError) {
      const validationErrors = {};
      validationError.inner.forEach(err => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
    }
  };

  return (
    <div className="profile-tab-container change-password-tab">
      <Form onSubmit={handleSubmit}>
        <div className="full-box-2 p-4 mt-2">
          <div className="input-group-item">
            <h2 className="fs-5 fw-semibold text-gray-800 mb-4">
              Change Password
            </h2>

            <Row className="mb-4">
              <Form.Group className="col-md-6" controlId="currentPassword">
                <Form.Label className="required">Current Password</Form.Label>
                <div className="input-warpper lock-icon">
                  <Form.Control
                    type={showPassword.currentPassword ? "text" : "password"}
                    placeholder="Enter current password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    isInvalid={!!errors.currentPassword}
                  />
                  <button
                    type="button"
                    className="input-group-icon btn-reset right"
                    onClick={(e) => PasswordToggle(e, 'currentPassword')}
                  >
                    <Image
                      src={showPassword.currentPassword ? "/images/eye.svg" : "/images/eye-off.svg"}
                      alt="Eye Icon"
                    />
                  </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.currentPassword}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Row>

            <Row className="gy-4">
              <Form.Group className="col-md-6" controlId="newPassword">
                <Form.Label className="required">New Password</Form.Label>
                <div className="input-warpper lock-icon">
                  <Form.Control
                    type={showPassword.newPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    isInvalid={!!errors.newPassword}
                  />
                  <button
                    type="button"
                    className="input-group-icon btn-reset right"
                    onClick={(e) => PasswordToggle(e, 'newPassword')}
                  >
                    <Image
                      src={showPassword.newPassword ? "/images/eye.svg" : "/images/eye-off.svg"}
                      alt="Eye Icon"
                    />
                  </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.newPassword}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>

              <Form.Group className="col-md-6" controlId="confirmNewPassword">
                <Form.Label className="required">Confirm New Password</Form.Label>
                <div className="input-warpper lock-icon">
                  <Form.Control
                    type={showPassword.confirmNewPassword ? "text" : "password"}
                    placeholder="Enter new password again"
                    name="confirmNewPassword"
                    value={formData.confirmNewPassword}
                    onChange={handleChange}
                    isInvalid={!!errors.confirmNewPassword}
                  />
                  <button
                    type="button"
                    className="input-group-icon btn-reset right"
                    onClick={(e) => PasswordToggle(e, 'confirmNewPassword')}
                  >
                    <Image
                      src={showPassword.confirmNewPassword? "/images/eye.svg" : "/images/eye-off.svg"}
                      alt="Eye Icon"
                    />
                  </button>
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmNewPassword}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Row>
          </div>
        </div>

        <div className="input-group-warpper d-flex align-align-items-center gap-2 p-4 mt-1">
          <Button variant="primary" className="btn-md" type="submit">
            Save & Update
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangePassword;
